<template>
  <section class="py-space-md px-space-xs py-md-space-lg" :style="backgroundStyle">
    <div class="container bg-dark-3 pt-space-sm pb-space-md px-space-md">
      <div class="row">
        <div class="col-12">
          <form id="asuo-rfi-static" data-cy="rfi-form" @submit.prevent="handleSubmit">
            <div class="text-white static-rfi">
              <!-- form fields -->
              <div id="form-fields">
                <p class="fs-small text-secondary fw-bold mb-space-xxxs">
                  Step {{ currentStep }} of 2
                </p>
                <h2 class="h2-small mb-space-xxs">Request Information</h2>
                <!-- step 1 -->
                <div v-show="currentStep === 1" id="step-1-form-fields">
                  <p class="fs-xs mb-space-xxs">
                    To learn more about ASU Online or a specific program, fill out the form below
                    and check your email for information on next steps.
                  </p>
                  <p class="fs-xs mb-space-sm">* Indicates a required field</p>
                  <!-- degree type -->
                  <div id="degree-type-group" class="fw-bold mb-space-sm">
                    <label
                      id="degree-type-label"
                      for="degree-type_typeahead__search"
                      class="mb-space-xxs"
                      ><span aria-hidden="true">*</span> Degree type</label
                    >

                    <TypeAheadSelect
                      id="degree-type"
                      v-model="form.degreeType as OptionType"
                      label-id="degree-type-label"
                      preselect-opacity=".75"
                      :options="degreeTypeOptions"
                      placeholder="Select a degree type"
                      data-cy="rfi-degree-type-filter"
                      class="fw-normal text-dark-1"
                      action-varint="light-5"
                      @changed="handleDegreeTypeChange"
                    >
                    </TypeAheadSelect>
                  </div>

                  <!-- interest areas -->
                  <div v-if="!isStudyPage" id="interest-area-group" class="fw-bold mb-space-sm">
                    <label
                      id="interest-area-label"
                      for="interest-area_typeahead__search"
                      class="mb-space-xxs"
                      >Area of interest</label
                    >

                    <TypeAheadSelect
                      id="interest-area"
                      v-model="form.interestArea as OptionType"
                      preselect-opacity=".75"
                      :options="interestAreaOptions"
                      :disabled="interestAreaDisabled"
                      placeholder="Select an interest area"
                      data-cy="rfi-interest-area-filter"
                      class="fw-normal text-dark-1"
                      action-varint="light-5"
                      label-id="interest-area-label"
                      @changed="handleInterestAreaChange"
                    >
                    </TypeAheadSelect>
                  </div>

                  <!-- programs -->
                  <div id="programs-group" class="fw-bold position-relative mb-0 pb-space-sm">
                    <label id="program-label" for="program_typeahead__search" class="mb-space-xxs">
                      <span v-if="programIsRequired" aria-hidden="true">* </span>Program
                    </label>

                    <TypeAheadSelect
                      id="program"
                      v-model="form.program as OptionType"
                      label-id="program-label"
                      preselect-opacity=".75"
                      :options="programOptions"
                      :disabled="programDisabled"
                      placeholder="Select a program"
                      data-cy="rfi-program-filter"
                      class="fw-normal text-dark-1"
                      action-varint="light-5"
                      @changed="handleProgramChange"
                    >
                    </TypeAheadSelect>
                  </div>

                  <!-- military radio group -->
                  <div
                    id="military-group"
                    role="radiogroup"
                    aria-labelledby="static-military-group-label"
                    class="form-check ps-0 position-relative mb-0 pb-space-sm d-flex flex-wrap"
                  >
                    <span
                      id="static-military-group-label"
                      class="fw-bold form-check-label fs-medium w-100"
                    >
                      * Have you served in the U.S. military or are you a military dependent?
                    </span>
                    <div v-for="option in militaryOptions" :key="option.label" class="form-check">
                      <label class="form-check-label">
                        <input
                          v-model="form.isMilitary"
                          name="military"
                          class="form-check-input"
                          type="radio"
                          :value="option.value"
                          @change="handleMilitaryClick()"
                        />
                        {{ option.label }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- step 2 -->
                <div v-show="currentStep === 2" id="step-2-form-fields">
                  <p class="fs-xs mb-space-sm">* Indicates a required field</p>
                  <!-- first-name input field -->
                  <div
                    id="first-name-group"
                    class="position-relative mb-0 pb-space-sm d-flex flex-column"
                  >
                    <label
                      id="static-rfi-first-name-label"
                      for="first-name"
                      class="fw-bold mb-space-xxs fs-xs"
                      ><span aria-hidden="true">*</span> First name</label
                    >
                    <input
                      id="first-name"
                      v-model.trim="form.firstName"
                      type="text"
                      aria-labelledby="static-rfi-first-name-label"
                      placeholder="Enter your first name"
                      data-cy="rfi-first-name"
                      class="form-control rounded-0"
                      :class="{
                        'is-invalid': firstNameErrorMessage
                      }"
                      :aria-invalid="firstNameErrorMessage ? 'true' : undefined"
                      :aria-describedby="
                        firstNameErrorMessage ? 'static-section-rfi-first-name-error' : undefined
                      "
                      :aria-errormessage="
                        firstNameErrorMessage ? 'static-section-rfi-first-name-error' : undefined
                      "
                      @blur="handleFirstNameFieldBlur"
                    />
                    <div
                      v-if="firstNameErrorMessage"
                      id="static-section-rfi-first-name-error"
                      class="invalid-feedback"
                      role="alert"
                      aria-live="assertive"
                      aria-atomic="true"
                      tabindex="0"
                    >
                      {{ firstNameErrorMessage }}
                    </div>
                  </div>
                  <!-- last-name input field -->
                  <div id="last-name-group" class="position-relative mb-0 pb-space-sm">
                    <label
                      id="static-rfi-last-name-label"
                      for="last-name"
                      class="fw-bold mb-space-xxs fs-xs"
                      ><span aria-hidden="true">*</span> Last name</label
                    >
                    <input
                      id="last-name"
                      v-model.trim="form.lastName"
                      type="text"
                      aria-labelledby="static-rfi-last-name-label"
                      placeholder="Enter your last name"
                      data-cy="rfi-last-name"
                      class="form-control rounded-0"
                      :class="{
                        'is-invalid': lastNameErrorMessage
                      }"
                      :aria-invalid="lastNameErrorMessage ? 'true' : undefined"
                      :aria-describedby="
                        lastNameErrorMessage ? 'static-section-rfi-last-name-error' : undefined
                      "
                      :aria-errormessage="
                        lastNameErrorMessage ? 'static-section-rfi-last-name-error' : undefined
                      "
                      @blur="handleLastNameFieldBlur"
                    />
                    <div
                      v-if="lastNameErrorMessage"
                      id="static-section-rfi-last-name-error"
                      class="invalid-feedback"
                      role="alert"
                      aria-live="assertive"
                      aria-atomic="true"
                      tabindex="0"
                    >
                      {{ lastNameErrorMessage }}
                    </div>
                  </div>

                  <!-- email input field -->
                  <div id="email-group" class="position-relative mb-0 pb-space-sm">
                    <label
                      id="static-rfi-email-label"
                      for="email"
                      class="fw-bold mb-space-xxs fs-xs"
                      ><span aria-hidden="true">*</span> Email</label
                    >
                    <input
                      id="email"
                      v-model.trim="form.email"
                      type="email"
                      aria-labelledby="static-rfi-email-label"
                      placeholder="Enter your email address"
                      class="form-control rounded-0"
                      :class="{
                        'is-invalid': emailErrorMessage
                      }"
                      :aria-invalid="emailErrorMessage ? 'true' : undefined"
                      :aria-describedby="
                        emailErrorMessage ? 'static-section-rfi-email-error' : undefined
                      "
                      :aria-errormessage="
                        emailErrorMessage ? 'static-section-rfi-email-error' : undefined
                      "
                      data-cy="rfi-email"
                      @input="handleEmailInput()"
                      @blur="handleEmailFieldBlur"
                    />
                    <div
                      v-if="emailErrorMessage"
                      id="static-section-rfi-email-error"
                      class="invalid-feedback"
                      role="alert"
                      aria-live="assertive"
                      aria-atomic="true"
                      tabindex="0"
                    >
                      {{ emailErrorMessage }}
                    </div>
                  </div>

                  <!-- phone number input field -->
                  <div id="phone-number-group" class="position-relative mb-0 pb-space-md">
                    <label for="asuonline_phone_number" class="fw-bold mb-space-xxs fs-xs"
                      ><span aria-hidden="true">*</span> Phone number</label
                    >
                    <ClientOnly>
                      <PhoneInputRfi
                        id="asuonline_phone_number"
                        v-model.trim="form.phone.number"
                        class="w-100"
                        :class="{ 'is-invalid': displayedPhoneError }"
                        :error="displayedPhoneError != ''"
                        :no-validator-state="true"
                        color="dark"
                        :aria-invalid="displayedPhoneError ? 'true' : undefined"
                        :aria-describedby="
                          displayedPhoneError ? 'static-section-rfi-phone-number-error' : undefined
                        "
                        :aria-errormessage="
                          displayedPhoneError ? 'static-section-rfi-phone-number-error' : undefined
                        "
                        @update="handlePhoneUpdate"
                        @blur="handlePhoneFieldBlur"
                      >
                      </PhoneInputRfi>
                    </ClientOnly>
                    <div
                      v-if="displayedPhoneError"
                      id="static-section-rfi-phone-number-error"
                      ref="phoneErrorRef"
                      class="invalid-feedback mb-space-xxs"
                      role="alert"
                      aria-live="assertive"
                      aria-atomic="true"
                      tabindex="0"
                    >
                      {{ displayedPhoneError }}
                    </div>
                  </div>

                  <!-- consent block -->
                  <div class="fs-xs mb-space-sm mb-md-space-md gdpr-links">
                    <text-gdpr class="link-color" text-size="gdpr"></text-gdpr>
                  </div>
                  <input id="cq_req_id" type="hidden" name="cq_req_id" />
                  <input id="cq_req_ip" type="hidden" name="cq_req_ip" />
                </div>
              </div>
              <!-- submit block -->
              <div class="row gx-0">
                <div class="col-12">
                  <div class="d-flex align-items-center" :class="submitContainerClass">
                    <button
                      v-show="currentStep === 2"
                      type="button"
                      aria-label="go to previous step"
                      class="text-white btn p-0"
                      data-cy="rfi-previous-step-button"
                      @click="previousStep"
                    >
                      <font-awesome-icon
                        icon="arrow-left"
                        size="1x"
                        alt="back icon"
                      ></font-awesome-icon>
                    </button>
                    <button
                      v-show="currentStep === 1"
                      data-cy="rfi-continue-button"
                      :disabled="!stepOneIsValid"
                      type="button"
                      class="fw-bold py-space-xxs px-space-md btn btn-secondary"
                      @click="nextStep"
                    >
                      Continue
                    </button>
                    <button
                      v-show="currentStep === 2"
                      class="fw-bold py-space-xxs px-space-md btn btn-secondary"
                      type="submit"
                      variant="secondary"
                      :disabled="submitDisabled"
                      data-cy="rfi-submit-button"
                    >
                      Submit
                      <div v-if="showLoader" class="d-inline">
                        <span
                          class="spinner-grow spinner-grow-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span class="sr-only">Loading...</span>
                        <span
                          class="spinner-grow spinner-grow-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span class="sr-only">Loading...</span>
                        <span
                          class="spinner-grow spinner-grow-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span class="sr-only">Loading...</span>
                      </div>
                    </button>
                    <div
                      v-if="submissionError"
                      class="invalid-feedback d-block mt-space-xxs"
                      role="alert"
                      aria-live="assertive"
                      aria-atomic="true"
                      tabindex="0"
                    >
                      {{ submissionError }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import TextGdpr from "~/components/rfi/TextGdpr.vue";

import type { DegreeSeekingProgram } from "~/server/resolvers/resolvers-types";
import TypeAheadSelect from "../TypeAheadSelect.vue";
import PhoneInputRfi from "./PhoneInputRfi.vue";

interface OptionType {
  text: string;
  value: string;
}

interface Props {
  bgColor?: string;
  isStudyPage?: boolean;
  programList?: DegreeSeekingProgram[];
}

const props = withDefaults(defineProps<Props>(), {
  bgColor: "var(--rds-light-2, #f1f1f1)",
  isStudyPage: false
});

const {
  form,
  lastNameErrorMessage,
  emailErrorMessage,
  phoneApiError,
  submissionError,
  firstNameErrorMessage,
  stepOneIsValid,
  programIsRequired,
  showLoader,
  submitDisabled,
  currentProgramCode,
  currentProgramName,
  degreeTypeOptions,
  programOptions,
  interestAreaOptions,
  interestAreaDisabled,
  programDisabled,
  handleDegreeTypeChange,
  handleEmailBlur: trackEmailBlur,
  handleEmailInput,
  handleFirstNameBlur: trackFirstNameBlur,
  handleInterestAreaChange,
  handleLastNameBlur: trackLastNameBlur,
  handleMilitaryClick,
  handlePhoneBlur: trackPhoneBlur,
  handlePhoneUpdate: updatePhoneState,
  handleProgramChange,
  syncResolvedStepOneSelection,
  handleSubmit
} = useRfi({
  isStudyPage: props.isStudyPage,
  programList: props.programList,
  enableUndecidedFlow: true
});

const currentStep = ref(1);
const phoneTouched = ref(false);
const phoneBlurred = ref(false);
const phoneErrorRef = ref<HTMLElement | null>(null);

const displayedPhoneError = computed(() => {
  if (phoneApiError.value) {
    return phoneApiError.value;
  }

  if (phoneBlurred.value && !form.value.phone.number) {
    return "Please enter a valid phone number and try again.";
  }

  if (phoneTouched.value && form.value.phone.number && form.value.phone.isValid === false) {
    return "Please enter a valid phone number and try again.";
  }

  return "";
});

const militaryOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false }
];

const submitContainerClass = computed(() => {
  return currentStep.value === 1 ? "justify-content-end" : "justify-content-between";
});

const backgroundStyle = computed(() => {
  return { "background-color": props.bgColor };
});

const previousStep = () => {
  currentStep.value = currentStep.value - 1;
  analyticsComposable.trackLinkEvent("internal link", "main content", "request info", "back arrow");
};

const handleFirstNameFieldBlur = () => {
  form.value.firstName = form.value.firstName ?? "";
  trackFirstNameBlur();
};

const handleLastNameFieldBlur = () => {
  form.value.lastName = form.value.lastName ?? "";
  trackLastNameBlur();
};

const handleEmailFieldBlur = () => {
  form.value.email = form.value.email ?? "";
  trackEmailBlur();
};

const handlePhoneFieldBlur = () => {
  phoneTouched.value = true;
  phoneBlurred.value = true;
  trackPhoneBlur();
  window.setTimeout(() => {
    if (displayedPhoneError.value) {
      phoneErrorRef.value?.focus();
    }
  }, 0);
};

const handlePhoneUpdate = (payload: Parameters<typeof updatePhoneState>[0]) => {
  phoneTouched.value = phoneTouched.value || Boolean(payload.nationalNumber);
  phoneBlurred.value = false;
  updatePhoneState(payload);
};

const nextStep = () => {
  syncResolvedStepOneSelection();

  if (currentProgramName.value && currentProgramCode.value) {
    if (props.isStudyPage) {
      analyticsComposable.trackEvent({
        event: "form",
        name: "onclick",
        action: "click",
        type: "click",
        region: "main content",
        section: "request info",
        text: "continue",
        component: "rfi step 1 of 2",
        degree_type:
          form.value.degreeType && form.value.degreeType.value
            ? form.value.degreeType.value.toLowerCase()
            : "select a degree type",
        program: currentProgramName.value.toLowerCase(),
        program_code: currentProgramCode.value,
        military_service: form.value.isMilitary === false ? "no" : "yes"
      });
    } else {
      analyticsComposable.trackEvent({
        event: "form",
        name: "onclick",
        action: "click",
        type: "click",
        region: "main content",
        section: "request info",
        text: "continue",
        component: "rfi-hero",
        degree_type:
          form.value.degreeType && form.value.degreeType.value
            ? form.value.degreeType.value.toLowerCase()
            : "select a degree type",
        area_of_interest:
          form.value.interestArea && form.value.interestArea.value
            ? form.value.interestArea.value.toLowerCase()
            : "select an interest area",
        program: currentProgramName.value.toLowerCase(),
        program_code: currentProgramCode.value,
        military_service: form.value.isMilitary === false ? "no" : "yes"
      });
    }
    currentStep.value = currentStep.value + 1;
  } else {
    console.error("missing program name or code, when proceeding to next step");
  }
};
</script>

<style lang="scss" scoped>
// Maz input customizations
:deep() {
  .m-phone-number-input button {
    background-color: transparent;
  }
  .m-phone-number-input {
    --maz-color-bg: var(--rds-dark-3);
    --maz-color-muted: rgba(255, 255, 255, 0.75);
    --maz-color-text: var(--rds-white);
    --maz-border-radius: 0;
  }
  .m-select .m-select-list-item.--is-selected {
    color: var(--rds-dark-3) !important;
    background-color: var(--rds-white) !important;
  }
  .m-select .m-select-list-item {
    color: var(--rds-white) !important;
  }
  .m-select .m-select-list-item:hover {
    color: var(--rds-dark-3) !important;
  }
  .m-input-wrapper {
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0;
    --maz-color-primary: var(--rds-secondary);
  }
}
// form check css
.form-check-input:checked {
  color: var(--rds-dark-3);
  --rds-form-check-bg-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
  --rds-form-check-bg: var(--rds-secondary);
  background-color: var(--rds-secondary);
  border-color: var(--rds-secondary);
}
.form-check {
  gap: 1rem;
}

// max phone input style

// form input css
.form-control {
  border-top: none;
  border-left: none;
  border-right: none;
  padding-top: 0px;
  padding-left: 0px;
  background-color: var(--rds-dark-3, #191919);
  color: #fafafa;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}
.form-control:focus {
  color: #ffc627;
  border-color: #ffc627;
  box-shadow: none;
  outline: none;
}

// Typeahead css
:deep(div.typeahead__dropdown-toggle) {
  background-color: var(--rds-dark-3, #191919);
  color: white;
  border-radius: 0;
  border-top-color: transparent;
  border-left-color: transparent;
  border-right-color: transparent;
}
:deep(div.typeahead__action) {
  color: white;
}
:deep(.link-color a) {
  color: #ffc627;
}

div.invalid-feedback {
  color: #ff7b7d !important;
}
</style>
